import React, { useState } from "react";
import { Box, Chip, Typography, Skeleton, Button, IconButton, Tooltip } from "@mui/material";
import { AccessTime, Boy, Girl, Receipt, LocalHospital, Business, CalendarToday, MedicalServices, Phone, Fax, Email, AccountBalance, Edit, AutoFixHigh } from "@mui/icons-material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import FlatCard from "../../../atoms/FlatCard";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import { useRx } from "../../../stores/Rx/rx.queries";
import { TherapyDto, TicketDto, UserDto } from "../../../api-client";
import useStore from "../../../helpers/useStore";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { PatientCommands } from "../../../stores/Patients/patient.commands";
import { PATIENT_SUBTABS } from "../../../stores/Patients/patientsStore";
import { useMinimalInsuranceTransactionbyId } from "../../../stores/Transactions/transaction.queries";
import { BillFullScreenDialog } from "../../Transactions/Insurance/Base/dialogs/BillFullScreenDialog";
import { ExportModalV2 } from "../../Transactions/Insurance/Base/components/ExportModal/v2/ExportModalV2";
import DoctorClinicDialog from "../../DoctorManagement/DoctorClinicDialog/DoctorClinicDialog";
import LinkagesEditor, { LinkageChanges } from "./LinkagesEditor";
import { slotTherapy } from "../../../helpers/slotTherapy";
import { therapyRxTermLabel } from "../../../helpers/therapyRxLabel";

const chipStyles = {
  height: "auto",
  margin: "1px",
  "& .MuiChip-label": {
    display: "block",
    whiteSpace: "normal",
    padding: "2px",
    minWidth: "60px",
  },
};

const infoBoxStyles = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  padding: 0.5,
  borderRadius: 1,
  backgroundColor: "rgba(0, 0, 0, 0.02)",
  border: "1px solid rgba(0, 0, 0, 0.08)",
};

const clickableBoxStyles = {
  ...infoBoxStyles,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    borderColor: "rgba(0, 0, 0, 0.16)",
  },
};

interface RxBoxProps {
  rxNumber?: string;
  date?: string | null;
  therapies?: string;
  onClick?: () => void;
  // MD-109: when set, the displayed Rx was inferred (targetRx). Shows a magic-wand
  // marker next to the number with this tooltip explaining requested vs. target.
  targetTooltip?: string;
}

// Shared presentation for every Rx linked to a ticket, whether it is the
// ticket's direct Rx or one reached indirectly through the appointment's
// timeslots. Both render identically and navigate to the Rx on click.
const RxBox: React.FC<RxBoxProps> = ({ rxNumber, date, therapies, onClick, targetTooltip }) => (
  <Box sx={onClick ? clickableBoxStyles : infoBoxStyles} onClick={onClick}>
    <Receipt />
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body1" fontWeight="medium">
          {rxNumber}
        </Typography>
        {targetTooltip && (
          <Tooltip arrow title={targetTooltip}>
            <AutoFixHigh
              sx={{ fontSize: 18, color: "#7c3aed", cursor: "help" }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        )}
      </Box>
      {date && (
        <Typography variant="body2" color="text.secondary">
          <CalendarToday sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
          {format(new Date(date), "dd.MM.yyyy")}
        </Typography>
      )}
      {therapies && (
        <Typography variant="body2" color="text.secondary">
          <MedicalServices sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
          {therapies}
        </Typography>
      )}
    </Box>
  </Box>
);

const Involvements = ({
  ticketData,
  editable = false,
  onLinkagesChange,
  onNavigate,
  onPendingLinkagesChange,
}: {
  ticketData: TicketDto;
  editable?: boolean;
  onLinkagesChange?: (changes: Partial<TicketDto>) => void | Promise<void>;
  onNavigate?: () => void;
  // MD-334: forwarded straight from the linkage editor so the enclosing ticket
  // form can prompt before closing on top of edits that were never saved.
  onPendingLinkagesChange?: (pending: { save: () => Promise<void> } | null) => void;
}) => {
  const navigate = useNavigate();
  const patientsStore = useStore(PatientsContext);
  const { navigateToPatient } = PatientCommands(patientsStore);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [clinicModalOpen, setClinicModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // MD-109: For Rx order tickets the backend may infer a preferred "target" Rx
  // (the perpetual/DT Rezept or an undated Rx in the same series). When present
  // we display that targetRx in the "Betroffen" area instead of the requested Rx,
  // marked with a magic-wand icon. The originally requested Rx stays on the ticket.
  const hasTargetRx = Boolean(ticketData?.targetRxId);
  const effectiveRxId = ticketData?.targetRxId || ticketData?.rxId || "";

  // Fetch full Rx data for the Rx we actually display (target when inferred).
  const { data: rxData, isLoading: rxLoading } = useRx(effectiveRxId);
  const { data: insuranceTransactionData, isLoading: insuranceTransactionLoading } = useMinimalInsuranceTransactionbyId(ticketData?.transactionId || "");

  // A doctor/clinic can be linked directly on the ticket (manual linkage). When
  // present these take precedence over the doctor/clinic derived from the Rx.
  const doctor = ticketData?.doctor || rxData?.doctor;
  const clinic = ticketData?.clinic || rxData?.clinic;

  const handleSaveLinkages = async (changes: LinkageChanges) => {
    if (onLinkagesChange) {
      await onLinkagesChange(changes as Partial<TicketDto>);
    }
    setIsEditing(false);
  };

  const handlePatientClick = () => {
    if (ticketData?.patientId) {
      navigateToPatient(navigate, ticketData.patientId, null);
    }
  };

  const navigateToRx = (rxId?: string) => {
    if (ticketData?.patientId && rxId) {
      onNavigate?.();
      navigateToPatient(navigate, ticketData.patientId, rxId, PATIENT_SUBTABS.PRESCRIPTIONS);
    }
  };

  // MD-109: navigate to the Rx we actually display (the inferred target when present).
  const handleRxClick = () => navigateToRx(effectiveRxId);

  const handleTransactionClick = () => {
    if (ticketData?.transactionId) {
      setExportModalOpen(true);
    }
  };

  const handleExportModalClose = () => {
    setExportModalOpen(false);
  };

  const handleClinicClick = () => {
    if (clinic) {
      setClinicModalOpen(true);
    }
  };

  // Rxs indirectly linked through the appointment's timeslots, deduped by rxId
  // and excluding the ticket's direct rxId (shown once via the main Rx box).
  const directRxId = ticketData?.rxId ? String(ticketData.rxId) : null;
  const timeslotRxMap = new Map<string, { rxId: string; rxNumber?: string; abbreviations: Set<string> }>();
  ticketData?.appointment?.timeSlots?.forEach((ts) => {
    const rx = ts?.therapyRx?.rx;
    if (!rx?.id) return;
    const rxId = String(rx.id);
    if (rxId === directRxId) return;
    if (!timeslotRxMap.has(rxId)) {
      timeslotRxMap.set(rxId, { rxId, rxNumber: rx.rxNumber, abbreviations: new Set() });
    }
    const abbreviation = (slotTherapy(ts) as TherapyDto)?.abbreviation;
    if (abbreviation) timeslotRxMap.get(rxId)!.abbreviations.add(abbreviation);
  });
  const timeslotRxList = Array.from(timeslotRxMap.values());

  // Therapists assigned to the appointment's timeslots, deduped by user id,
  // shown next to the appointment time (without repeating Rx/therapy).
  const timeslotTherapistMap = new Map<string, UserDto>();
  ticketData?.appointment?.timeSlots?.forEach((ts) => {
    const user = ts?.user;
    if (!user?.id) return;
    const userId = String(user.id);
    if (timeslotTherapistMap.has(userId)) return;
    timeslotTherapistMap.set(userId, user);
  });
  const timeslotTherapistList = Array.from(timeslotTherapistMap.values());

  let content = null;

  if (ticketData) {
    content = (
      <>
        {/* Info Boxes Container */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, marginBottom: 1 }}>
        {/* Patient Box */}
        {ticketData.patientId && ticketData.patient && (
          <Box sx={clickableBoxStyles} onClick={handlePatientClick}>
            {ticketData.patient.salutation === "Frau" ? (
              <Girl  />
            ) : (
              <Boy  />
            )}
            <Box>
              <Typography variant="body1" fontWeight="medium">
                {`${ticketData.patient.firstName} ${ticketData.patient.lastName}`}
              </Typography>
              {ticketData.patient.dob && (
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(ticketData.patient.dob), "dd.MM.yyyy")}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Rx Box */}
        {ticketData.rxId && (
          <>
            {rxLoading ? (
              <Box sx={infoBoxStyles}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="60%" />
              </Box>
            ) : (
              rxData && (
                <RxBox
                  rxNumber={rxData.rxNumber}
                  date={rxData.date}
                  therapies={rxData.therapyRxs
                    ?.map((t) => `${t.amount}x${therapyRxTermLabel(t)}`)
                    .filter(Boolean)
                    .join(", ")}
                  onClick={handleRxClick}
                  targetTooltip={
                    hasTargetRx
                      ? `Angefragtes Rezept: ${ticketData.rx?.rxNumber || ticketData.rxId}. Für die Dateneingabe wird automatisch das Ziel-Rezept ${rxData.rxNumber} aus derselben Serie verwendet (Dauerrezept bzw. undatiertes Rezept).`
                      : undefined
                  }
                />
              )
            )}
          </>
        )}

        {/* Rx Boxes for Rxs linked via the appointment's timeslots */}
        {timeslotRxList.map((rx) => (
          <RxBox
            key={rx.rxId}
            rxNumber={rx.rxNumber}
            therapies={Array.from(rx.abbreviations).join(", ")}
            onClick={() => navigateToRx(rx.rxId)}
          />
        ))}

        {/* Doctor/Clinic Box */}
        {(doctor || clinic) && (
          <Box sx={clickableBoxStyles} onClick={handleClinicClick}>
            <LocalHospital  />
            <Box>
              {doctor && (
                <Typography variant="body1" fontWeight="medium">
                  Dr.{" "}
                  {`${doctor.contactInfo?.firstNames || ""} ${
                    doctor.contactInfo?.lastName || ""
                  }`.trim()}
                </Typography>
              )}
              {clinic && (
                <>
                  {clinic.contactInfo?.businessName && (
                    <Typography variant="body2" color="text.secondary">
                      <Business sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                      {clinic.contactInfo.businessName}
                    </Typography>
                  )}
                  {clinic.contactInfo?.phone1 && (
                    <Typography variant="body2" color="text.secondary">
                      <Phone sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                      {clinic.contactInfo.phone1}
                    </Typography>
                  )}
                  {clinic.contactInfo?.fax && (
                    <Typography variant="body2" color="text.secondary" sx={{fontWeight: clinic?.methodForDrReport?.toLowerCase() === "fax" ? "bold" : "normal"}}>
                      <Fax sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                      {clinic.contactInfo.fax}
                    </Typography>
                  )}
                   {clinic.contactInfo?.email && (
                    <Typography variant="body2" color="text.secondary" sx={{fontWeight: clinic?.methodForDrReport?.toLowerCase() ==="email" ? "bold" : "normal"}}>
                      <Email sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                      {clinic.contactInfo.email}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}
        </Box>
        {/* Transaction Box */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, marginBottom: 1 }}>
        {ticketData.transactionId && (
          <>
            {insuranceTransactionLoading ? (
              <Box sx={infoBoxStyles}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width="60%" />
              </Box>
            ) : (
              insuranceTransactionData && (
                <Box sx={clickableBoxStyles} onClick={handleTransactionClick}>
                  <AccountBalance />
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {insuranceTransactionData.transactionNumber}
                    </Typography>
                    {insuranceTransactionData.user.firstName && insuranceTransactionData.user.lastName && (
                      <Typography variant="body2" color="text.secondary">
                        {`${insuranceTransactionData.user.firstName} ${insuranceTransactionData.user.lastName}`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            )}
          </>
        )}
        </Box>
        

        {/* Appointment Data */}
        {ticketData.appointmentId && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Start time with the assigned therapist(s) on one line */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            {ticketData.appointment?.start && (
              <Chip
                sx={chipStyles}
                label={
                  <>
                    <AccessTime />
                    {format(new Date(ticketData.appointment?.start), "dd.MM.yyyy, HH:mm")}
                  </>
                }
              />
            )}
              {timeslotTherapistList.map((therapist, index) => (
                <MobiliAvatar
                  key={index}
                  user={therapist}
                  isTherapist
                  sx={{ width: 32, height: 32, fontSize: "0.8rem" }}
                />
              ))}
            </Box>
          </Box>
        )}
      </>
    );
  }
  return (
    <>
      <FlatCard
        title="Betroffen"
        action={
          editable && !isEditing ? (
            <Tooltip title="Verknüpfungen bearbeiten">
              <IconButton size="small" onClick={() => setIsEditing(true)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
        sx={{
          minHeight: "100px",
          width: "100%",
          flexGrow: "1",
          overflow: "auto", // Enable overflow scrolling
        }}
      >
        {isEditing ? (
          <LinkagesEditor
            patientId={ticketData?.patientId}
            patient={ticketData?.patient}
            rxId={ticketData?.rxId}
            rxNumber={rxData?.rxNumber}
            // MD-335: the editor has to see the same doctor the card shows. A
            // ticket usually inherits its doctor from the Rx rather than linking
            // one itself, and passing only ticketData.doctorId left the field
            // empty in exactly that - normal - case, while doctorLabel below was
            // already resolved from the effective doctor.
            doctorId={ticketData?.doctorId || doctor?.id}
            doctorLabel={
              doctor
                ? `Dr. ${doctor.contactInfo?.firstNames || ""} ${
                    doctor.contactInfo?.lastName || ""
                  }`.trim()
                : undefined
            }
            clinicId={ticketData?.clinicId || clinic?.id}
            onSave={handleSaveLinkages}
            onCancel={() => setIsEditing(false)}
            onPendingChange={onPendingLinkagesChange}
          />
        ) : content ? (
          content
        ) : (
          ""
        )}
      </FlatCard>

      {/* Export Modal */}
      {insuranceTransactionData && (
        <BillFullScreenDialog
          open={exportModalOpen}
          onClose={handleExportModalClose}
          titleComp={<span>Abrechnung (Exportiert)</span>}
          actions={
            <Button variant="text" onClick={handleExportModalClose}>
              Schließen
            </Button>
          }
        >

          <ExportModalV2
            transaction={insuranceTransactionData}
          />
        </BillFullScreenDialog>
      )}

      {/* Clinic Modal */}
      {clinic && (
        <DoctorClinicDialog
          openAddEditDialog={clinicModalOpen}
          handleCloseAddEditDialog={() => setClinicModalOpen(false)}
          onDataSaved={() => {}}
          clinicId={clinic.id || (clinic as any)._id}
        />
      )}
    </>
  );
};

export default Involvements;
